import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { History } from "lucide-react";
import { useState, useEffect } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { useGetAllFees } from "@features/cx/settings/hooks";

const FeesHistoryModal = ({
  openModal,
  setOpenModal,
}: {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: MODAL_PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  const { data: allFees } = useGetAllFees(openModal,searchQuery,currentPage);

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = allFees?.count;
    const totalPages = Math.ceil(totalItems / MODAL_PAGE_SIZE);
    const startItem = (currentPage - 1) * MODAL_PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * MODAL_PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: MODAL_PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [allFees?.count, currentPage]);

  const columns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Fee Name",
      sortable: true,
    },
    {
      key: "amount",
      header: "Amount",
      sortable: true,
    },
    {
      key: "description",
      header: "Description",
      sortable: true,
    },
    {
      key: "categoryName",
      header: "Category",
      sortable: true,
    },
    {
      key: "createdBy",
      header: "Created By",
      sortable: true,
    },
    {
      key: "createdDate",
      header: "Created On",
      sortable: true,
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
    setCurrentPage(1); // Reset to first page on new search
  };

  return (
    <div>
      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <History className="h-5 w-5" />
              Fees History
            </DialogTitle>
            <DialogDescription>
              Complete history of all fee configurations including who created
              them and when.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <GenericTable
              data={allFees?.results || []}
              columns={columns}
              loading={false}
              searchPlaceholder="Search fees..."
              searchParamName="feeQuery"
              onSearch={handleSearch}
              pagination={pagination}
              onPageChange={handlePageChange}
              searchDebounceMs={300}
              rowKey="id"
              emptyMessage="No fees found"
            />
          </div>

          <DialogFooter>
            <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeesHistoryModal;
